import React from 'react';
import { useTranslation } from 'react-i18next';
import { Radio, Shield, AlertTriangle } from 'lucide-react';
import { TtnReadiness } from '../../../../types/clientOnboarding';

interface Props {
  data: TtnReadiness;
}

const STATUS_ORDER: (keyof TtnReadiness)[] = [
  'application', 'webhook', 'gateway', 'end_devices',
  'commands', 'downlinks', 'commissioning',
];

const statusColor = (status: string): string => {
  switch (status) {
    case 'NOT_CONFIGURED': return 'text-gray-500 bg-gray-100';
    case 'TO_VERIFY': return 'text-amber-700 bg-amber-100';
    case 'AWAITING_HARDWARE': return 'text-blue-700 bg-blue-100';
    case 'DISABLED': return 'text-red-700 bg-red-100';
    default: return 'text-gray-500 bg-gray-100';
  }
};

const TtnReadinessStep: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation(['admin_client_onboarding']);

  const sectionLabels: Record<keyof TtnReadiness, string> = {
    application: t('step7.application'),
    webhook: t('step7.webhook'),
    gateway: t('step7.gateway'),
    end_devices: t('step7.endDevices'),
    commands: t('step7.commands'),
    downlinks: t('step7.downlinks'),
    commissioning: t('step7.commissioning'),
  };

  const statusLabels: Record<string, string> = {
    NOT_CONFIGURED: t('step7.status.NOT_CONFIGURED'),
    TO_VERIFY: t('step7.status.TO_VERIFY'),
    AWAITING_HARDWARE: t('step7.status.AWAITING_HARDWARE'),
    DISABLED: t('step7.status.DISABLED'),
  };

  return (
    <div className="space-y-4" data-testid="onboarding-step-ttn">
      <div>
        <h3 className="text-heading-3 text-[var(--text-primary)] mb-1">{t('step7.heading')}</h3>
        <p className="text-sm text-[var(--text-secondary)]">{t('step7.description')}</p>
      </div>

      <div className="space-y-2">
        {STATUS_ORDER.map(key => (
          <div key={key} className="flex items-center justify-between p-3 border border-[var(--border-light)] rounded-lg" data-testid={`ttn-${key}`}>
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4 text-[var(--text-tertiary)]" />
              <span className="text-sm font-medium text-[var(--text-primary)]">{sectionLabels[key]}</span>
            </div>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColor(data[key])}`}>
              {statusLabels[data[key]]}
            </span>
          </div>
        ))}
      </div>

      <button
        disabled
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[var(--gray-100)] text-[var(--text-tertiary)] rounded-lg cursor-not-allowed text-sm"
        title={t('step7.testButtonTooltip')}
        data-testid="onboarding-ttn-test-btn"
      >
        <Radio className="w-4 h-4" /> {t('step7.testButton')}
      </button>

      <div className="flex items-start gap-2 p-3 rounded-lg bg-[var(--color-info)]/10 border border-[var(--color-info)]/30 text-sm text-[var(--text-secondary)]" data-testid="onboarding-ttn-safety">
        <Shield className="w-4 h-4 mt-0.5 shrink-0 text-[var(--color-info)]" />
        <p>{t('step7.safetyNote')}</p>
      </div>
    </div>
  );
};

export default TtnReadinessStep;
